import Vue from 'vue'
import router from '@/router'
import { TOOLS, RADAR_EXECUTION_INTERVAL_MS } from '@/constants.js'
import { Api } from '@/core/api.js'
import { RADAR_API_TOOL_SETTERS, RADAR_LOCAL_ONLY_TOOL_CODES, RADAR_MODULE_ORDER, RADAR_MODULE_SCHEMA, RADAR_SUPPORTED_TOOL_CODES, RADAR_TOOL_STORE_MAP, moduleSchemaHasTotal } from '@/store/utils/radarSchema.js'

/*
 * Herramientas soportadas por Radar (TOOLS.* → código numérico):
 * - TOOLS.SENSORS              → 13
 * - TOOLS.CHECKLISTS           → 1
 * - TOOLS.ACTION_PLANS         → 19
 * - TOOLS.ISSUES               → 2
 * - TOOLS.REGISTERS            → 4
 * - TOOLS.PRODUCT_TIMERS       → 16 (se sincroniza localmente, es decir no se pide al endpoint /radar)
 * - TOOLS.OPERATIONAL_TIMERS   → 15 (se sincroniza localmente, es decir no se pide al endpoint /radar)
 */

const api = new Api()

/** Frecuencia de sincronización independiente por herramienta. */
const RADAR_TOOL_SYNC_INTERVALS_MS = {
    [TOOLS.SENSORS]: 15 * 60 * 1000,
    [TOOLS.CHECKLISTS]: 15 * 60 * 1000,
    [TOOLS.REGISTERS]: 30 * 60 * 1000,
    [TOOLS.ACTION_PLANS]: 30 * 60 * 1000,
    [TOOLS.ISSUES]: 30 * 60 * 1000,
    [TOOLS.OPERATIONAL_TIMERS]: 30 * 60 * 1000,
    [TOOLS.PRODUCT_TIMERS]: 30 * 60 * 1000
}

let lastRadarSyncExecutionAt = null
let radarSyncPromise = null
/** Promesa de la carga inicial de Radar en curso; deduplica llamadas concurrentes a loadRadarData (BUILD-7459). */
let radarLoadPromise = null
/**
 * BUILD-7459: "generación" de la sesión de Radar. Se incrementa en cada resetState (logout, cambio de tienda
 * o Radar deshabilitado). Una loadRadarData en vuelo captura la generación al iniciar y descarta su resultado
 * si cambió, para no pisar datos de la nueva sesión ni bloquear su carga con hasCompletedInitialLoad.
 */
let radarLoadGeneration = 0
/**
 * BUILD-7459: true una vez que la primera loadRadarData de la sesión empezó a ejecutarse (con éxito o no).
 * Mientras sea false, syncRadar cede el primer /radar a loadRadarData (evita duplicar la carga inicial). Una vez
 * true, syncRadar puede operar con normalidad, incluida la recuperación si la carga inicial falló. Se reinicia
 * en resetState (logout, cambio de tienda o Radar deshabilitado).
 */
let initialRadarLoadStarted = false
/** Orden de prioridad de severity para ordenar KPIs (menor índice = mayor prioridad). */
const KPI_SEVERITY_ORDER = ['critical', 'warning', 'info']

/** Índice de prioridad de una severity; las desconocidas (p.ej. null) se envían al final. */
const getKpiSeverityPriority = (severity) => {
    const index = KPI_SEVERITY_ORDER.indexOf(severity)

    return index === -1 ? KPI_SEVERITY_ORDER.length : index
}

/** Mapea la severity de un KPI a la clase CSS del icono en SummaryCard. */
const resolveKpiIconColor = (severity) => {
    if (severity === 'critical') {
        return 'error'
    }

    if (severity === 'warning') {
        return 'warning'
    }

    return 'neutral'
}

/** Ordena KPIs por severity (critical > warning > info) y, a igual severity, por count descendente. */
const sortKpisBySeverityAndCount = (kpis) => {
    const result = [...kpis].sort((kpiA, kpiB) => {
        const severityDiff = getKpiSeverityPriority(kpiA.severity) - getKpiSeverityPriority(kpiB.severity)

        if (severityDiff !== 0) {
            return severityDiff
        }

        return kpiB.count - kpiA.count
    })

    return result
}

// -------------------------------------------------------------------
/*
 * DESCOMENTAR SOLO PARA PRUEBAS DE FRONTEND — descomentar este bloque y las líneas return en getRadarViewData
 * para sobreescribir count e isVisible que recibe Radar.vue sin tocar el API ni el state.
 */

// const RADAR_FRONTEND_TEST_COUNT_OVERRIDES = {
//     criticalCards: {
//         sensors_with_issues: 4,
//         sensors_without_data: 5,
//         tasks_to_justify_yesterday: 3,
//         tasks_to_justify_today: 4,
//         product_timers_expired: 0,
//         operational_timers_expired: 0
//     },
//     modules: {
//         [TOOLS.SENSORS]: {
//             sensors_with_issues: 4,
//             sensors_without_data: 5,
//             sensors_with_alert: 6,
//             sensors_muted: 2
//         },
//         [TOOLS.CHECKLISTS]: {
//             tasks_to_justify_yesterday: 7,
//             tasks_to_justify_today: 8,
//             tasks_expiring_soon: 9
//         },
//         [TOOLS.PRODUCT_TIMERS]: {
//             product_timers_expired: 0,
//             product_timers_expiring_soon: 0
//         },
//         [TOOLS.ACTION_PLANS]: {
//             open_action_plans: 12
//         },
//         [TOOLS.ISSUES]: {
//             open_issues: 13
//         },
//         [TOOLS.REGISTERS]: {
//             draft_registers: 14
//         },
//         [TOOLS.OPERATIONAL_TIMERS]: {
//             operational_timers_expired: 0
//         }
//     }
// }

/** DESCOMENTAR SOLO PARA PRUEBAS DE FRONTEND. Construye modules y criticalCards desde el schema + overrides, sin depender del API ni del state. */
// const applyRadarFrontendTestCountOverrides = ({ viewModules, criticalCards } = {}) => {
//     const overrides = RADAR_FRONTEND_TEST_COUNT_OVERRIDES

//     if (criticalCards && overrides.criticalCards) {
//         criticalCards.forEach((card) => {
//             if (Object.prototype.hasOwnProperty.call(overrides.criticalCards, card.kpiKey)) {
//                 const overrideCount = overrides.criticalCards[card.kpiKey]
//                 Object.assign(card, { count: overrideCount, isVisible: overrideCount > 0 })
//             }
//         })
//     }

//     if (viewModules && overrides.modules) {
//         viewModules.forEach((viewModule) => {
//             const moduleOverrides = overrides.modules[viewModule.toolCode]

//             if (!moduleOverrides) {
//                 return
//             }

//             viewModule.kpis.forEach((kpi) => {
//                 if (Object.prototype.hasOwnProperty.call(moduleOverrides, kpi.key)) {
//                     const overrideCount = moduleOverrides[kpi.key]
//                     Object.assign(kpi, { count: overrideCount, isVisible: overrideCount > 0 })
//                 }
//             })

//             viewModule.kpis = sortKpisBySeverityAndCount(viewModule.kpis)
//         })
//     }

//     return { viewModules, criticalCards }
// }

/** Nivel de rol con acceso a todas las herramientas del local en Radar. */
const ADMIN_ROLE_LEVEL = 9

/** Devuelve array filtrado con los códigos de herramienta radar habilitados para la tienda loggeada.
 * Puede ser array vacío si no hay herramientas habilitadas.
 */
const buildRadarEnabledToolCodes = (rootGetters) => {
    const locationToolCodes = new Set(rootGetters['login/getEnabledToolCodes'])

    const result = RADAR_SUPPORTED_TOOL_CODES.filter((toolCode) => {
        return locationToolCodes.has(toolCode)
    })

    return result
}

/**
 * BUILD-7459: peticiones /radar en vuelo, indexadas por el conjunto de herramientas solicitado. Coalesce
 * peticiones idénticas concurrentes en una sola llamada de red, de modo que los múltiples disparos de la
 * carga inicial (varios load() + posibles resetState transitorios al asentarse la config) generen una única
 * petición al endpoint /radar en lugar de duplicarla.
 */
const inFlightRadarRequests = new Map()

/** Clave estable e independiente del orden para un conjunto de toolCodes. */
const buildRadarRequestKey = (apiTools) => {
    return apiTools
        .map(Number)
        .sort((toolA, toolB) => toolA - toolB)
        .join(',')
}

/** Realiza la petición real a /radar y devuelve la respuesta cruda (o null si falla). */
const requestRadarApiData = async (apiTools) => {
    try {
        const response = await api.post('radar', { tools: apiTools })

        if (!response.status) {
            console.error(response.msg || 'Error fetching radar data') // eslint-disable-line no-undef

            return null
        }

        return response.data || {}
    } catch (error) {
        console.error(error) // eslint-disable-line no-undef

        return null
    }
}

/** Llama al API radar (coalesciendo peticiones idénticas en vuelo) y devuelve la respuesta cruda (sin normalizar).
 * Devuelve null si la petición falla (el error se registra en consola). */
const fetchRadarApiData = (apiTools) => {
    if (!apiTools || apiTools.length === 0) {
        return Promise.resolve({})
    }

    const requestKey = buildRadarRequestKey(apiTools)
    const inFlightRequest = inFlightRadarRequests.get(requestKey)

    if (inFlightRequest) {
        return inFlightRequest
    }

    const request = requestRadarApiData(apiTools).finally(() => {
        inFlightRadarRequests.delete(requestKey)
    })

    inFlightRadarRequests.set(requestKey, request)

    return request
}

/** Filtra las herramientas habilitadas según el rol del empleado loggeado. */
const filterEnabledToolCodesByEmployeeRole = (enabledToolCodes, rootGetters) => {
    const roleLevel = rootGetters['loginUser/getUserRoleLevel']

    if (roleLevel === ADMIN_ROLE_LEVEL) {
        return enabledToolCodes
    }

    if (roleLevel === null) {
        return []
    }

    const roleToolCodes = new Set(rootGetters['getAccessibleToolCodesByRoleLevel'](roleLevel))

    const result = enabledToolCodes.filter((toolCode) => roleToolCodes.has(toolCode))

    return result
}

/** Persiste la respuesta cruda del API en el módulo Vuex correspondiente a cada herramienta. */
const commitRadarApiDataToModules = (commit, apiData) => {
    if (!apiData || typeof apiData !== 'object') {
        return []
    }

    return Object.entries(apiData).reduce((syncedToolCodes, [toolCode, rawModuleData]) => {
        const numericToolCode = Number(toolCode)
        const mutation = RADAR_API_TOOL_SETTERS[numericToolCode]

        if (mutation) {
            try {
                commit(mutation, rawModuleData, { root: true })
                syncedToolCodes.push(numericToolCode)
            } catch (error) {
                console.error(`Error saving Radar tool ${numericToolCode}:`, error)
            }
        }

        return syncedToolCodes
    }, [])
}

/** Devuelve el count guardado de "por justificar ayer"; null si el KPI todavía no fue cargado. */
const getTasksToJustifyYesterdayCount = (rootState) => {
    const radarKPIs = rootState.tasks && rootState.tasks.radarKPIs

    if (!Array.isArray(radarKPIs)) {
        return null
    }

    const kpi = radarKPIs.find((item) => item && item.key === 'tasks_to_justify_yesterday')

    if (!kpi) {
        return null
    }

    return Array.isArray(kpi.templates) ? kpi.templates.length : 0
}

/** Indica si una herramienta nunca se sincronizó o ya cumplió su frecuencia. */
const shouldSyncRadarTool = (state, toolCode, now) => {
    const interval = RADAR_TOOL_SYNC_INTERVALS_MS[toolCode]
    const lastSyncedAt = state.toolLastSyncedAt[toolCode]
    return interval && (lastSyncedAt === undefined || now - lastSyncedAt >= interval)
}

/**
 * BUILD-7499: refresca Timers operacionales en el store (fuente del KPI local).
 * Solo true si loadTimers completó API + getLastTimesDone + updateTimers (no marcar lastSyncedAt en fallos).
 * @returns {boolean}
 */
const syncOperationalTimersForRadar = async (dispatch) => {
    try {
        const synced = await dispatch('timer/loadTimers', {}, { root: true })

        return synced === true
    } catch (error) {
        console.error('Error syncing Radar operational timers:', error)

        return false
    }
}

/**
 * BUILD-7500: refresca Timers de producto en el store (fuente de los KPIs locales).
 * Solo true si loadTimers completó API + processTimers (no marcar lastSyncedAt en fallos).
 * @returns {boolean}
 */
const syncProductTimersForRadar = async (dispatch) => {
    try {
        const synced = await dispatch('timerproduct/loadTimers', {}, { root: true })

        return synced === true
    } catch (error) {
        console.error('Error syncing Radar product timers:', error)

        return false
    }
}

/** Resetea radarKPIs en los módulos que reciben datos del endpoint /radar (antes de cada carga). */
const clearRadarApiModuleData = (commit) => {
    Object.values(RADAR_API_TOOL_SETTERS).forEach((mutation) => {
        commit(mutation, null, { root: true })
    })
}

/**
 * Recopila, por cada herramienta accesible, únicamente los valores dinámicos de sus KPIs
 * (los que varían en runtime: count, severity y total). No incluye metadata estática del
 * schema (labelKey, icon, route, etc.); eso se añade después en enrichModule/enrichKpi.
 *
 * @param {number[]} accessibleToolCodes
 * @param {object} rootGetters
 * @returns {Record<number, { kpis: Record<string, { count: number, severity: string|null }>, total?: number }>}
 * @example
 * {
 *   13: {
 *     total: 24,
 *     kpis: {
 *       sensors_with_issues:  { count: 3, severity: 'critical' },
 *       sensors_without_data: { count: 1, severity: 'critical' },
 *       sensors_with_alert:   { count: 5, severity: 'warning' },
 *       sensors_muted:        { count: 0, severity: 'info' }
 *     }
 *   },
 *   1: {
 *     kpis: {
 *       tasks_to_justify_yesterday: { count: 2, severity: 'critical' },
 *       tasks_to_justify_today:     { count: 0, severity: 'critical' },
 *       tasks_expiring_soon:        { count: 4, severity: 'warning' }
 *     }
 *   }
 * }
 */
const collectAccessibleModulesDynamicKpiValues = (accessibleToolCodes, rootGetters) => {
    const result = {}

    for (const toolCode of accessibleToolCodes) {
        const numericToolCode = Number(toolCode)
        const storeModule = RADAR_TOOL_STORE_MAP[numericToolCode]
        const moduleSchema = RADAR_MODULE_SCHEMA[numericToolCode]

        if (!storeModule || !moduleSchema) {
            continue
        }

        const kpiKeys = Object.keys(moduleSchema.kpis)

        const moduleState = rootGetters[`${storeModule}/getRadarModuleState`](kpiKeys)

        if (moduleState) {
            result[numericToolCode] = moduleState
        }
    }

    return result
}

/** Resuelve la ruta de un KPI. Si se pasa kpiKey en navegación, aplica filtros dinámicos (p. ej. propDate de ayer). */
const resolveKpiRoute = (kpiSchema, kpiKey) => {
    if (!kpiSchema || !kpiSchema.route) {
        return null
    }

    const routeConfig = kpiSchema.route
    const params = Object.assign({}, routeConfig.params || {})

    if (kpiKey === 'tasks_to_justify_yesterday' && typeof params.propDateDaysOffset === 'number') {
        params.propDate = moment().add(params.propDateDaysOffset, 'day').startOf('day') // eslint-disable-line no-undef
    }

    // clean params (Tasks.vue needs propDate, not propDateDaysOffset)
    if (typeof params.propDateDaysOffset === 'number') {
        delete params.propDateDaysOffset
    }

    const route = {
        name: routeConfig.name,
        params
    }

    if (routeConfig.query) {
        route.query = Object.assign({}, routeConfig.query)
    }

    return route
}

/** Obtiene el schema de un KPI a partir de toolCode y kpiKey. */
const getKpiSchema = (toolCode, kpiKey) => {
    const moduleSchema = RADAR_MODULE_SCHEMA[toolCode]
    const kpiSchema = moduleSchema && moduleSchema.kpis && moduleSchema.kpis[kpiKey]

    return kpiSchema || null
}

/** Combina metadata del schema con valores dinámicos del state para un KPI. */
const enrichKpi = (toolCode, kpiKey, kpiState, moduleState) => {
    const moduleSchema = RADAR_MODULE_SCHEMA[toolCode]
    const kpiSchema = moduleSchema && moduleSchema.kpis && moduleSchema.kpis[kpiKey]

    if (!kpiSchema || !kpiState) {
        return null
    }

    const count = Number(kpiState.count) || 0
    const enriched = {
        key: kpiKey,
        toolCode,
        kpiKey,
        icon: kpiSchema.icon || (moduleSchema && moduleSchema.icon) || '',
        labelKey: kpiSchema.labelKey,
        severity: kpiState.severity,
        iconColor: resolveKpiIconColor(kpiState.severity),
        route: resolveKpiRoute(kpiSchema),
        count,
        isVisible: count > 0
    }

    if (moduleSchemaHasTotal(moduleSchema)) {
        enriched.total = Number(moduleState && moduleState.total) || 0
    }

    return enriched
}

/** Enriquece un módulo accesible con metadata del schema para la vista. */
const enrichModule = (toolCode, moduleState) => {
    const moduleSchema = RADAR_MODULE_SCHEMA[toolCode]

    if (!moduleSchema || !moduleState || !moduleState.kpis) {
        return null
    }

    const unsortedKpis = Object.keys(moduleSchema.kpis).reduce((result, kpiKey) => {
        const enriched = enrichKpi(toolCode, kpiKey, moduleState.kpis[kpiKey], moduleState)

        if (enriched) {
            result.push(enriched)
        }

        return result
    }, [])

    const kpis = sortKpisBySeverityAndCount(unsortedKpis)

    const viewModule = {
        toolCode,
        labelKey: moduleSchema.labelKey,
        icon: moduleSchema.icon,
        badgeLabel: `radar.criticalLabel.${moduleSchema.key}`,
        kpis
    }

    return viewModule
}

/** Construye los módulos enriquecidos listos para la vista (orden según RADAR_MODULE_ORDER). */
const buildViewModules = (dynamicKpiValuesByToolCode) => {
    const result = RADAR_MODULE_ORDER.reduce((viewModules, toolCode) => {
        const moduleState = dynamicKpiValuesByToolCode[toolCode]
        const viewModule = enrichModule(toolCode, moduleState)

        if (viewModule) {
            viewModules.push(viewModule)
        }

        return viewModules
    }, [])

    return result
}

/** Suma el count de los KPIs con severity 'critical' de un módulo (se usa para el badgeCount de la card). */
const sumCriticalKpisCount = (kpis) => {
    return kpis.reduce((sum, kpi) => (kpi.severity === 'critical' ? sum + (Number(kpi.count) || 0) : sum), 0)
}

/**
 * Excluye módulos sin KPIs visibles y deja solo KPIs con isVisible === true.
 * Calcula badgeCount aquí (para la tag roja que indica el total de KPIs críticos)
 */
const filterViewModulesWithVisibleKpis = (viewModules) => {
    const result = viewModules.reduce((visibleModules, viewModule) => {
        const visibleKpis = viewModule.kpis.filter((kpi) => kpi.isVisible)

        if (visibleKpis.length > 0) {
            const badgeCount = sumCriticalKpisCount(viewModule.kpis)

            visibleModules.push({ ...viewModule, kpis: visibleKpis, badgeCount })
        }

        return visibleModules
    }, [])

    return result
}

/** Excluye critical cards con isVisible === false. */
const filterVisibleCriticalCards = (criticalCards) => {
    const result = criticalCards.filter((card) => card.isVisible)

    return result
}

/** Enriquece un KPI como critical card (icono del módulo, labelKey de criticalCard). */
const enrichCriticalCard = (toolCode, kpiKey, kpiState, moduleState) => {
    const moduleSchema = RADAR_MODULE_SCHEMA[toolCode]
    const kpiSchema = moduleSchema && moduleSchema.kpis && moduleSchema.kpis[kpiKey]
    const criticalCardLabelKey = kpiSchema && kpiSchema.criticalCard && kpiSchema.criticalCard.labelKey

    if (!criticalCardLabelKey || !kpiState) {
        return null
    }

    const enriched = enrichKpi(toolCode, kpiKey, kpiState, moduleState)

    if (!enriched || enriched.severity !== 'critical') {
        return null
    }

    const card = {
        toolCode,
        kpiKey,
        icon: moduleSchema.icon || '',
        labelKey: criticalCardLabelKey,
        count: enriched.count,
        isVisible: enriched.isVisible,
        route: enriched.route,
        severity: enriched.severity
    }

    if (typeof enriched.total !== 'undefined') {
        card.total = enriched.total
    }

    return card
}

/** Construye las critical cards: KPIs con criticalCard en schema y severity 'critical' resuelta en state. */
const buildCriticalCards = (dynamicKpiValuesByToolCode) => {
    const cards = []

    for (const toolCode of RADAR_MODULE_ORDER) {
        const moduleState = dynamicKpiValuesByToolCode[toolCode]
        const moduleSchema = RADAR_MODULE_SCHEMA[toolCode]

        if (!moduleSchema || !moduleState || !moduleState.kpis) {
            continue
        }

        for (const kpiKey of Object.keys(moduleSchema.kpis)) {
            const card = enrichCriticalCard(toolCode, kpiKey, moduleState.kpis[kpiKey], moduleState)

            if (card) {
                cards.push(card)
            }
        }
    }

    return cards
}

const getDefaultState = () => {
    return {
        isLoading: false,
        /** Timestamp (ms) de última sync por toolCode; solo se actualiza tras éxito en /radar o sync local. */
        toolLastSyncedAt: {},
        /** true cuando la última llamada al endpoint /radar falló; false cuando fue exitosa. */
        showRadarUpdateError: false,
        /**
         * BUILD-7459: true una vez que la carga inicial de Radar terminó con éxito. Evita que los múltiples
         * disparos de load() en la inicialización (created + watchers isLoaded/getToken) repitan la petición
         * a /radar. Se resetea con resetState (logout, cambio de tienda o deshabilitar Radar) y lo ignora el
         * refresco manual (force), de modo que no afecta ninguna recarga legítima.
         */
        hasCompletedInitialLoad: false
    }
}

const state = getDefaultState()

const getters = {
    /** Códigos de herramienta visibles para el empleado actual según su rol (nivel 9 = todas las de la tienda). */
    getEmployeeAccessibleToolCodes: (state, getters, rootState, rootGetters) => {
        const result = filterEnabledToolCodesByEmployeeRole(buildRadarEnabledToolCodes(rootGetters), rootGetters)

        return result
    },
    /**
     * Datos listos para la vista de Radar: módulos visuales y critical cards.
     * Recorre y transforma los módulos accesibles una sola vez.
     */
    getRadarViewData: (state, getters, rootState, rootGetters) => {
        const dynamicKpiValuesByToolCode = collectAccessibleModulesDynamicKpiValues(getters.getEmployeeAccessibleToolCodes, rootGetters)
        const viewModules = buildViewModules(dynamicKpiValuesByToolCode)
        const criticalCards = buildCriticalCards(dynamicKpiValuesByToolCode)

        // DESCOMENTAR SOLO PARA PRUEBAS DE FRONTEND. SOBREESCRIBE EL VALOR DE COUNT DE CADA KPI.
        // const overridden = applyRadarFrontendTestCountOverrides({ viewModules, criticalCards })

        // return {
        //     modules: filterViewModulesWithVisibleKpis(overridden.viewModules),
        //     criticalCards: filterVisibleCriticalCards(overridden.criticalCards)
        // }

        return {
            modules: filterViewModulesWithVisibleKpis(viewModules),
            criticalCards: filterVisibleCriticalCards(criticalCards)
        }
    },
    /** Devuelve true si Radar tiene al menos un módulo visual para mostrar al empleado actual. */
    getHasRadarContent: (state, getters) => {
        const radarViewData = getters.getRadarViewData

        return radarViewData.modules.length > 0
    },
    getIsLoading: (state) => {
        return state.isLoading
    },
    getShowRadarUpdateError: (state) => {
        return state.showRadarUpdateError
    },
    /** Timestamp de última sync de una herramienta; null si nunca se sincronizó. */
    getToolLastSyncedAt: (state) => (toolCode) => {
        const syncedAt = state.toolLastSyncedAt[Number(toolCode)]

        return syncedAt !== undefined ? syncedAt : null
    }
}

const actions = {
    resetState({ commit }) {
        // Invalida cualquier loadRadarData en vuelo y evita que la próxima carga reutilice su promesa obsoleta.
        radarLoadGeneration += 1
        radarLoadPromise = null
        initialRadarLoadStarted = false

        clearRadarApiModuleData(commit)
        commit('resetState')
    },
    /**
     * Navega a la herramienta configurada en el schema del KPI y aplica sus filtros (ruta + query/params).
     * Usada por módulos visuales y critical cards de Radar.
     */
    navigateToKpi(context, payload) {
        const toolCode = payload && payload.toolCode
        const kpiKey = (payload && payload.kpiKey) || (payload && payload.key)

        if (!toolCode || !kpiKey) {
            return Promise.resolve(false)
        }

        const kpiSchema = getKpiSchema(toolCode, kpiKey)
        const route = resolveKpiRoute(kpiSchema, kpiKey)

        if (!route || !route.name) {
            return Promise.resolve(false)
        }

        return router.push(route).catch((error) => {
            if (error && error.name !== 'NavigationDuplicated') {
                console.error('Error navigating to route:', error) // eslint-disable-line no-undef
            }
        })
    },
    /**
     * Llama al endpoint radar con los toolCodes indicados y registra la fecha de sync por herramienta en éxito.
     * Gestiona el estado showRadarUpdateError: true si el endpoint falla, false si tiene éxito.
     *
     * Acepta un array de toolCodes (uso legacy) o un objeto { apiTools, loadGeneration }. Cuando se pasa
     * loadGeneration, se descartan las mutaciones de estado si un resetState invalidó la carga mientras la
     * petición estaba en vuelo (BUILD-7459), para no contaminar la nueva sesión con timestamps/errores viejos.
     *
     * @returns {Record<string, object>|null} respuesta cruda del API indexada por toolCode, o null si falla o quedó obsoleta
     */
    async loadRadarApiData({ commit }, payload) {
        const apiTools = Array.isArray(payload) ? payload : payload && payload.apiTools
        const loadGeneration = Array.isArray(payload) ? null : payload && payload.loadGeneration

        if (!apiTools || apiTools.length === 0) {
            return null
        }

        const apiData = await fetchRadarApiData(apiTools)

        // Si un resetState invalidó esta carga mientras la petición estaba en vuelo, no mutamos el estado.
        if (loadGeneration !== null && loadGeneration !== undefined && radarLoadGeneration !== loadGeneration) {
            return null
        }

        if (apiData === null) {
            commit('setShowRadarUpdateError', true)

            return null
        }

        commit('setToolsLastSyncedAt', { toolCodes: apiTools, timestamp: Date.now() })
        commit('setShowRadarUpdateError', false)

        return apiData
    },
    /**
     * Evalúa las herramientas habilitadas que cumplieron su frecuencia y realiza una única
     * petición consolidada a /radar. La carga de Tareas usa su endpoint independiente.
     */
    syncRadar({ state, commit, dispatch, rootState, rootGetters }) {
        if (radarSyncPromise) {
            return radarSyncPromise
        }

        // BUILD-7459: syncRadar es una sincronización incremental POSTERIOR a la carga inicial. No debe lanzar
        // una petición /radar mientras haya una loadRadarData en vuelo (carga inicial o refresco manual), ni
        // antes de que la carga inicial de la sesión haya arrancado, para evitar peticiones duplicadas en el
        // arranque. Una vez arrancada esa carga, syncRadar opera normal (incluida la recuperación si falló).
        if (radarLoadPromise) {
            return Promise.resolve(false)
        }

        if (!initialRadarLoadStarted) {
            return Promise.resolve(false)
        }

        if (!rootGetters.getShowRadarTool) {
            return Promise.resolve(false)
        }

        const enabledTools = buildRadarEnabledToolCodes(rootGetters)

        if (enabledTools.length === 0) {
            return Promise.resolve(false)
        }

        const now = Date.now()

        if (lastRadarSyncExecutionAt !== null && now - lastRadarSyncExecutionAt < RADAR_EXECUTION_INTERVAL_MS) {
            return Promise.resolve(false)
        }

        lastRadarSyncExecutionAt = now

        radarSyncPromise = (async () => {
            const toolsToSync = enabledTools.filter((toolCode) => shouldSyncRadarTool(state, toolCode, now))
            const shouldSyncTasks = toolsToSync.includes(TOOLS.CHECKLISTS)
            const tasksToJustifyYesterdayCount = shouldSyncTasks ? getTasksToJustifyYesterdayCount(rootState) : null
            let tasksSynced = !shouldSyncTasks

            // console.log('[Radar sync] Ejecutando syncRadar. Candidatas:', formatRadarToolCodesForLog(toolsToSync))

            // if (toolsToSync.length === 0) {
            //     console.log('[Radar sync] Ninguna herramienta cumplió su frecuencia. Skip.')
            //     return false
            // }

            if (shouldSyncTasks) {
                try {
                    await dispatch(
                        'tasks/loadAll',
                        {
                            cType: 1,
                            date: moment().unix(), // eslint-disable-line no-undef
                            forceUpdate: true
                        },
                        { root: true }
                    )
                    tasksSynced = true
                } catch (error) {
                    console.error('Error syncing Radar tasks:', error)
                }
            }

            if (!rootGetters.getShowRadarTool) {
                return false
            }

            const apiTools = toolsToSync.filter((toolCode) => {
                return !RADAR_LOCAL_ONLY_TOOL_CODES.includes(toolCode) && (toolCode !== TOOLS.CHECKLISTS || tasksToJustifyYesterdayCount !== 0)
            })

            if (apiTools.length > 0) {
                const apiData = await fetchRadarApiData(apiTools)

                if (apiData !== null) {
                    const savedToolCodes = commitRadarApiDataToModules(commit, apiData)
                    const syncedToolCodes = savedToolCodes.filter((toolCode) => toolCode !== TOOLS.CHECKLISTS || tasksSynced)

                    commit('setToolsLastSyncedAt', { toolCodes: syncedToolCodes, timestamp: Date.now() })
                    commit('setShowRadarUpdateError', false)
                    // console.log('[Radar sync] Sincronización completada. Herramientas sincronizadas:', formatRadarToolCodesForLog(syncedToolCodes))
                } else {
                    commit('setShowRadarUpdateError', true)
                    // console.warn('[Radar sync] Error al obtener datos del API. Herramientas pedidas:', formatRadarToolCodesForLog(apiTools))
                }
            }

            if (shouldSyncTasks && tasksToJustifyYesterdayCount === 0 && tasksSynced) {
                commit('setToolsLastSyncedAt', { toolCodes: [TOOLS.CHECKLISTS], timestamp: Date.now() })
            }

            // refrescar store de timers cuando toque por frecuencia
            if (toolsToSync.includes(TOOLS.OPERATIONAL_TIMERS)) {
                const operationalTimersSynced = await syncOperationalTimersForRadar(dispatch)

                if (operationalTimersSynced) {
                    commit('setToolsLastSyncedAt', { toolCodes: [TOOLS.OPERATIONAL_TIMERS], timestamp: Date.now() })
                }
            }

            // BUILD-7500: refrescar store de timers de producto cuando toque por frecuencia
            if (toolsToSync.includes(TOOLS.PRODUCT_TIMERS)) {
                const productTimersSynced = await syncProductTimersForRadar(dispatch)

                if (productTimersSynced) {
                    commit('setToolsLastSyncedAt', { toolCodes: [TOOLS.PRODUCT_TIMERS], timestamp: Date.now() })
                }
            }

            return true
        })()

        return radarSyncPromise.finally(() => {
            radarSyncPromise = null
        })
    },
    /**
     * Carga inicial completa de Radar: obtiene datos del endpoint /radar y los persiste
     * sin transformación en el módulo Vuex de cada herramienta.
     *
     * @returns {boolean} true si hubo herramientas habilitadas y la carga terminó sin error
     */
    loadRadarData({ state, commit, dispatch, rootGetters }, payload) {
        // BUILD-7459: durante la inicialización, load() de Default-Layout puede dispararse varias veces
        // (created + watchers isLoaded/getToken). Garantizamos una única petición a /radar en la carga inicial:
        //  1) radarLoadPromise deduplica llamadas concurrentes (patrón de syncRadar).
        //  2) hasCompletedInitialLoad deduplica llamadas secuenciales (una ya terminó antes de la siguiente).
        // El refresco manual pasa force: true para saltar ambos guards y recargar siempre.
        const force = !!(payload && payload.force)

        if (radarLoadPromise) {
            return radarLoadPromise
        }

        if (!force && state.hasCompletedInitialLoad) {
            return Promise.resolve(true)
        }

        // La carga inicial de la sesión ya arrancó: a partir de aquí syncRadar puede operar (coalescing +
        // radarLoadPromise evitan duplicados y, si esta carga falla, syncRadar podrá actuar como recuperación).
        initialRadarLoadStarted = true

        // Generación de esta carga: si resetState la incrementa mientras la petición está en vuelo, la carga
        // quedó obsoleta (logout, cambio de tienda o Radar deshabilitado) y no debe aplicar sus efectos.
        const loadGeneration = radarLoadGeneration
        const isStaleLoad = () => radarLoadGeneration !== loadGeneration

        const currentLoadPromise = (async () => {
            const enabledTools = buildRadarEnabledToolCodes(rootGetters)

            if (!enabledTools || (enabledTools && enabledTools.length === 0)) {
                clearRadarApiModuleData(commit)
                commit('clearToolLastSyncedAt')

                return false
            }

            commit('setIsLoading', true)

            try {
                clearRadarApiModuleData(commit)

                const apiTools = enabledTools.filter((toolCode) => {
                    return !RADAR_LOCAL_ONLY_TOOL_CODES.includes(toolCode)
                })

                // loadRadarApiData/fetchRadarApiData devuelven null (no lanzan) cuando /radar falla, así que
                // hay que detectar el fallo explícitamente para no marcar la carga inicial como completada.
                let apiFetchFailed = false

                if (apiTools && apiTools.length > 0) {
                    const apiData = await dispatch('loadRadarApiData', { apiTools, loadGeneration })

                    // Si hubo un reset mientras la petición estaba en vuelo, descartamos el resultado para no
                    // pisar los datos de la nueva sesión ni bloquear su carga con hasCompletedInitialLoad.
                    // (loadRadarApiData ya omitió por su cuenta las mutaciones de timestamps/error.)
                    if (isStaleLoad()) {
                        return false
                    }

                    if (apiData === null) {
                        apiFetchFailed = true
                    } else {
                        commitRadarApiDataToModules(commit, apiData)
                    }
                }

                // BUILD-7404: el KPI "por justificar ayer" se calcula en el front (plantillas distintas con
                // checklists de ayer por justificar). Se dispara en segundo plano (sin await) para no retrasar
                // el resto del Radar.
                if (enabledTools.includes(TOOLS.CHECKLISTS)) {
                    dispatch('tasks/loadTasksToJustifyYesterdayKpi', null, { root: true })
                }

                // Timers operacionales (KPI local).
                // - force (botón Actualizar): recarga timers desde API.
                // - carga inicial: Default-Layout ya hace loadTimers; solo marcamos sync para no
                //   repetir la petición en el primer syncRadar (lastSyncedAt undefined).
                if (enabledTools.includes(TOOLS.OPERATIONAL_TIMERS)) {
                    if (force) {
                        const operationalTimersSynced = await syncOperationalTimersForRadar(dispatch)

                        if (!isStaleLoad() && operationalTimersSynced) {
                            commit('setToolsLastSyncedAt', { toolCodes: [TOOLS.OPERATIONAL_TIMERS], timestamp: Date.now() })
                        }
                    } else if (!isStaleLoad()) {
                        commit('setToolsLastSyncedAt', { toolCodes: [TOOLS.OPERATIONAL_TIMERS], timestamp: Date.now() })
                    }
                }

                // BUILD-7500: Timers de producto (KPIs locales).
                // - force (botón Actualizar): recarga timers desde API + processTimers.
                // - carga inicial: Default-Layout ya hace loadTimerProducts/loadTimers; solo marcamos sync
                //   para no repetir la petición en el primer syncRadar (lastSyncedAt undefined).
                if (enabledTools.includes(TOOLS.PRODUCT_TIMERS)) {
                    if (force) {
                        const productTimersSynced = await syncProductTimersForRadar(dispatch)

                        if (!isStaleLoad() && productTimersSynced) {
                            commit('setToolsLastSyncedAt', { toolCodes: [TOOLS.PRODUCT_TIMERS], timestamp: Date.now() })
                        }
                    } else if (!isStaleLoad()) {
                        commit('setToolsLastSyncedAt', { toolCodes: [TOOLS.PRODUCT_TIMERS], timestamp: Date.now() })
                    }
                }

                // Solo marcamos la carga inicial como completada si /radar no falló (ni por excepción ni por
                // fallo "suave" que devuelve null), para que un error de red permita reintentar en lugar de
                // quedar bloqueado por el guard hasCompletedInitialLoad.
                if (!apiFetchFailed) {
                    commit('setHasCompletedInitialLoad', true)
                }

                return true
            } catch (error) {
                console.error(error) // eslint-disable-line no-undef

                return false
            } finally {
                // No tocamos isLoading si la carga quedó obsoleta: resetState ya reinició el estado y podría
                // haber una nueva carga en curso.
                if (!isStaleLoad()) {
                    commit('setIsLoading', false)
                }
            }
        })()

        radarLoadPromise = currentLoadPromise

        return currentLoadPromise.finally(() => {
            // Solo liberamos la referencia si sigue siendo esta carga (resetState pudo anularla o reemplazarla).
            if (radarLoadPromise === currentLoadPromise) {
                radarLoadPromise = null
            }
        })
    }
}

const mutations = {
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    setIsLoading(state, value) {
        state.isLoading = value
    },
    setToolsLastSyncedAt(state, { toolCodes, timestamp }) {
        if (!toolCodes || toolCodes.length === 0) {
            return
        }

        toolCodes.forEach((toolCode) => {
            Vue.set(state.toolLastSyncedAt, Number(toolCode), timestamp)
        })
    },
    clearToolLastSyncedAt(state) {
        Vue.set(state, 'toolLastSyncedAt', {})
    },
    setShowRadarUpdateError(state, value) {
        state.showRadarUpdateError = value
    },
    setHasCompletedInitialLoad(state, value) {
        state.hasCompletedInitialLoad = value
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
